import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {buildLocalizedMessageWarning} from '../../../../srcCommon/inboxes/composer/composerWarnings';
import {MessageWarning} from '../../../helpers/models/messageHelpers';
import {OkCancelDialog} from '../../common/dialogs/okCancelDialog';

/*
 * Props.
 */

interface ComposerWarningsDialogProps {
  warnings: ReadonlyArray<MessageWarning>;
  onRequestClose: Handler;
  onOkClick: Handler;
}

/*
 * Style.
 */

const StyledEmphasisSpan = styled.span`
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

/* eslint react/no-array-index-key: 0 */
export const ComposerWarningsDialog: FC<ComposerWarningsDialogProps> = ({warnings, ...props}) => {
  if (warnings.length === 0) {
    throw new Error('No warnings were provided');
  }

  const renderEmphasisText = (content: ReactNode) => <StyledEmphasisSpan>{content}</StyledEmphasisSpan>;

  // Single warning case, customized title and Ok button.
  if (warnings.length === 1) {
    const warning = warnings[0];
    const localizedWarning = buildLocalizedMessageWarning(warning, renderEmphasisText);

    return (
      <OkCancelDialog
        title={localizedWarning.title}
        okButtonLabel={<Trans id="warning-generic-ok">Send</Trans>}
        onRequestClose={props.onRequestClose}
        onOkClick={props.onOkClick}
      >
        {localizedWarning.text}
        <br />
        <Trans id="warning-generic-prompt">Would you like to send it anyway?</Trans>
      </OkCancelDialog>
    );
  }

  // Multiple warnings case, generic title and Ok button.
  return (
    <OkCancelDialog
      title={<Trans id="warning-generic-title">Reminder</Trans>}
      okButtonLabel={<Trans id="warning-generic-ok">Send</Trans>}
      onRequestClose={props.onRequestClose}
      onOkClick={props.onOkClick}
    >
      <ul>
        {warnings.map((warning, index) => (
          <li key={index}>{buildLocalizedMessageWarning(warning, renderEmphasisText).text}</li>
        ))}
      </ul>
    </OkCancelDialog>
  );
};
